import { useEffect, useState, useCallback } from 'react'
import { Flag, ImageOff, Check, X, ExternalLink, Trash2, Archive, Lock } from 'lucide-react'
import NotificationSettings from '../components/NotificationSettings'

interface Report {
  id: string
  eventId: string
  eventName: string
  sessionId: string
  photoId: string
  photoUrl: string | null
  photoType: string | null
  scope: 'photo' | 'session'
  sessionPhotoCount: number
  quarantinedCount: number
  reason: string
  note: string | null
  status: 'open' | 'resolved' | 'dismissed'
  createdAt: string
  resolvedAt: string | null
}

interface QuarantinedPhoto {
  id: string
  sessionId: string
  type: string | null
  filename: string
  capturedAt: string | null
  deletedAt: string
  url: string
  thumbnailUrl: string
  available: boolean
}

const REASON_LABELS: Record<string, string> = {
  inappropriate: 'Inappropriate content',
  not_mine: "Not the guest's photo",
  technical_issue: 'Technical issue',
  other: 'Other',
}

type Filter = 'open' | 'resolved' | 'dismissed' | 'all'

export default function ReportsPage() {
  const [reports, setReports] = useState<Report[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<Filter>('open')
  const [updating, setUpdating] = useState<string | null>(null)

  // Deleted-photo viewer: quarantined files aren't publicly reachable, so the
  // only way to review a takedown is through the authenticated API.
  const [viewing, setViewing] = useState<Report | null>(null)
  const [quarantined, setQuarantined] = useState<QuarantinedPhoto[] | null>(null)

  const load = useCallback(() => {
    setLoading(true)
    const qs = filter === 'all' ? '' : `?status=${filter}`
    fetch(`/api/reports${qs}`)
      .then((r) => r.json())
      .then(setReports)
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [filter])

  useEffect(() => {
    load()
  }, [load])

  const openDeleted = (report: Report) => {
    setViewing(report)
    setQuarantined(null)
    fetch(`/api/reports/quarantine?sessionId=${encodeURIComponent(report.sessionId)}`)
      .then((r) => r.json())
      .then(setQuarantined)
      .catch((err) => {
        console.error('Failed to load deleted photos:', err)
        setQuarantined([])
      })
  }

  const act = async (
    report: Report,
    status: Report['status'],
    action: 'keep' | 'delete_photo' | 'delete_session' = 'keep'
  ) => {
    if (
      action === 'delete_photo' &&
      !confirm('Remove this photo from the gallery? The file is kept in admin-only storage, where you can still review it.')
    ) {
      return
    }
    if (
      action === 'delete_session' &&
      !confirm(
        `Remove all ${report.sessionPhotoCount} photo${report.sessionPhotoCount === 1 ? '' : 's'} in this session from the gallery? ` +
          'The files are kept in admin-only storage, where you can still review them.'
      )
    ) {
      return
    }

    setUpdating(report.id)
    try {
      const res = await fetch(`/api/reports/${report.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, action }),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => null)
        alert(body?.error || 'Failed to update the report.')
        return
      }

      if (action === 'keep') {
        // Optimistic removal when filtered to a specific status so acting on
        // a report doesn't leave it sitting in a list it no longer belongs to.
        if (filter !== 'all') {
          setReports((prev) => prev.filter((r) => r.id !== report.id))
        } else {
          setReports((prev) => prev.map((r) => (r.id === report.id ? { ...r, status } : r)))
        }
      } else {
        // A delete also closes other reports on the same session and invalidates
        // their thumbnails, so refetch rather than guess at the new state.
        load()
      }
    } catch (err) {
      console.error('Failed to update report:', err)
      alert('Failed to update the report.')
    } finally {
      setUpdating(null)
    }
  }

  const tabs: { key: Filter; label: string }[] = [
    { key: 'open', label: 'Open' },
    { key: 'resolved', label: 'Resolved' },
    { key: 'dismissed', label: 'Dismissed' },
    { key: 'all', label: 'All' },
  ]

  return (
    <div className="p-6 max-w-4xl space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold">
          <Flag className="h-5 w-5 text-primary" />
          Reported Photos
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Photos guests have flagged from the public gallery.
        </p>
      </div>

      <NotificationSettings />

      <div className="inline-flex rounded-xl border border-border bg-card/50 p-1">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={`rounded-lg px-4 py-1.5 text-sm font-semibold transition-colors ${
              filter === tab.key ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center gap-3 py-16 text-muted-foreground">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-border border-t-primary" />
          Loading reports...
        </div>
      ) : reports.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border py-20 text-center">
          <Flag className="mb-3 h-8 w-8 text-muted-foreground/40" />
          <p className="font-medium text-muted-foreground">No {filter === 'all' ? '' : filter} reports</p>
          <p className="mt-1 text-sm text-muted-foreground/60">Guest-reported photos will show up here.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reports.map((r) => (
            <div key={r.id} className="flex gap-4 rounded-xl border border-border bg-card/50 p-4">
              <div className="h-20 w-20 shrink-0 overflow-hidden rounded-lg border border-border bg-background">
                {r.photoUrl ? (
                  <img src={r.photoUrl} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-muted-foreground/40">
                    <ImageOff className="h-6 w-6" />
                  </div>
                )}
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-red-500/10 px-2.5 py-0.5 text-xs font-semibold text-red-400">
                    {REASON_LABELS[r.reason] || r.reason}
                  </span>
                  {/* Session reports cover the whole set — the thumbnail is only
                      a stand-in, so say so rather than implying one bad photo. */}
                  <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
                    {r.scope === 'session'
                      ? `Whole session · ${r.sessionPhotoCount} photo${r.sessionPhotoCount === 1 ? '' : 's'}`
                      : 'Single photo'}
                  </span>
                  <span className="text-xs text-muted-foreground">{r.eventName}</span>
                  <span className="text-xs text-muted-foreground/50">
                    {new Date(r.createdAt).toLocaleString()}
                  </span>
                  {r.status !== 'open' && (
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                      r.status === 'resolved' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-muted text-muted-foreground'
                    }`}>
                      {r.status}
                    </span>
                  )}
                </div>

                {r.note && <p className="mt-2 text-sm text-foreground/80">"{r.note}"</p>}

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {/* Only link to the gallery while something is actually left
                      there — otherwise it opens an empty page. */}
                  {r.sessionPhotoCount > 0 && (
                    <a
                      href={`/gallery?eventId=${r.eventId}&sessionId=${r.sessionId}`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                    >
                      <ExternalLink className="h-3.5 w-3.5" /> View session
                    </a>
                  )}

                  {/* Deleted photos are kept in admin-only storage, so a
                      takedown stays reviewable after the fact. */}
                  {r.quarantinedCount > 0 && (
                    <button
                      onClick={() => openDeleted(r)}
                      className="flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                    >
                      <Archive className="h-3.5 w-3.5" /> View deleted ({r.quarantinedCount})
                    </button>
                  )}

                  {r.status === 'open' && (
                    <>
                      <button
                        onClick={() => act(r, 'resolved')}
                        disabled={updating === r.id}
                        className="flex items-center gap-1 rounded-lg bg-emerald-500/15 border border-emerald-500/20 px-3 py-1.5 text-xs font-semibold text-emerald-400 hover:bg-emerald-500/25 disabled:opacity-50 transition-colors"
                      >
                        <Check className="h-3.5 w-3.5" /> Resolve &amp; keep
                      </button>

                      {/* Only offered for single-photo reports: a session report
                          doesn't identify one photo — its thumbnail is just a
                          stand-in for the set. */}
                      {r.scope === 'photo' && !!r.photoUrl && (
                        <button
                          onClick={() => act(r, 'resolved', 'delete_photo')}
                          disabled={updating === r.id}
                          className="flex items-center gap-1 rounded-lg bg-red-500/15 border border-red-500/20 px-3 py-1.5 text-xs font-semibold text-red-400 hover:bg-red-500/25 disabled:opacity-50 transition-colors"
                        >
                          <Trash2 className="h-3.5 w-3.5" /> Delete photo
                        </button>
                      )}

                      {r.sessionPhotoCount > 0 && (
                        <button
                          onClick={() => act(r, 'resolved', 'delete_session')}
                          disabled={updating === r.id}
                          className="flex items-center gap-1 rounded-lg bg-red-500/15 border border-red-500/20 px-3 py-1.5 text-xs font-semibold text-red-400 hover:bg-red-500/25 disabled:opacity-50 transition-colors"
                        >
                          <Trash2 className="h-3.5 w-3.5" /> Delete session (
                          {r.sessionPhotoCount})
                        </button>
                      )}

                      <button
                        onClick={() => act(r, 'dismissed')}
                        disabled={updating === r.id}
                        className="flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:bg-muted disabled:opacity-50 transition-colors"
                      >
                        <X className="h-3.5 w-3.5" /> Dismiss
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {viewing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
          onClick={() => setViewing(null)}
        >
          <div
            className="max-h-[85vh] w-full max-w-3xl overflow-y-auto rounded-2xl border border-border bg-card p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="flex items-center gap-2 text-lg font-bold">
                  <Archive className="h-4.5 w-4.5 text-primary" />
                  Deleted photos
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  {viewing.eventName} · session {viewing.sessionId}
                </p>
              </div>
              <button
                onClick={() => setViewing(null)}
                aria-label="Close"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-border text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <p className="mt-3 flex items-start gap-2 rounded-xl border border-border bg-background/50 p-3 text-xs text-muted-foreground">
              <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              These files were removed from the gallery but kept on the server. They are
              only reachable here, by signed-in admins — not from any public URL.
            </p>

            {quarantined === null ? (
              <div className="flex items-center gap-3 py-12 text-muted-foreground">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-border border-t-primary" />
                Loading…
              </div>
            ) : quarantined.length === 0 ? (
              <p className="py-12 text-center text-sm text-muted-foreground">
                Nothing in storage for this session.
              </p>
            ) : (
              <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
                {quarantined.map((q) => (
                  <div key={q.id} className="overflow-hidden rounded-xl border border-border bg-background">
                    {q.available ? (
                      <a href={q.url} target="_blank" rel="noreferrer" title="Open full size">
                        <img
                          src={q.thumbnailUrl}
                          alt={q.filename}
                          className="aspect-square w-full object-cover transition-opacity hover:opacity-80"
                        />
                      </a>
                    ) : (
                      <div className="flex aspect-square w-full flex-col items-center justify-center gap-1 text-muted-foreground/40">
                        <ImageOff className="h-6 w-6" />
                        <span className="text-[10px]">file missing</span>
                      </div>
                    )}
                    <div className="p-2">
                      <p className="truncate text-xs font-medium" title={q.filename}>
                        {q.filename}
                      </p>
                      <p className="mt-0.5 text-[10px] text-muted-foreground">
                        {q.type === 'layout' ? 'Print composite · ' : ''}
                        deleted {new Date(q.deletedAt).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
